/**
 * Code couleur paiement RDV — présentation uniquement.
 * S'appuie sur getRdvPaymentView (contrat API #642), sans nouvelle déduction financière.
 */

import { getRdvPaymentView } from './rdvPaymentDisplay.js';
import { rdvNeedsPaymentFollowUp } from './rdvPaymentFollowUp.js';

/** @typedef {'success' | 'warning' | 'danger' | 'neutral'} RdvPaymentTone */

const TONE_CHIP = {
  success: 'bg-emerald-50 text-emerald-800',
  warning: 'bg-amber-50 text-amber-900',
  danger: 'bg-rose-50 text-rose-900',
  neutral: 'bg-slate-50 text-slate-700',
};

const TONE_CELL = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  warning: 'bg-amber-50 border-amber-200 text-amber-950',
  danger: 'bg-rose-50 border-rose-200 text-rose-950',
  neutral: 'bg-slate-50 border-slate-200 text-slate-800',
};

const TONE_BADGE = {
  success: 'bg-emerald-50 text-emerald-800 border border-emerald-200',
  warning: 'bg-amber-50 text-amber-900 border border-amber-200',
  danger: 'bg-rose-50 text-rose-900 border border-rose-200',
  neutral: 'bg-slate-100 text-slate-800 border border-slate-200',
};

const TONE_PANEL = {
  success: 'border-emerald-200 bg-emerald-50/60',
  warning: 'border-amber-200 bg-amber-50/60',
  danger: 'border-rose-200 bg-rose-50/60',
  neutral: 'border-gray-200 bg-gray-50',
};

const TONE_PANEL_TITLE = {
  success: 'text-emerald-800',
  warning: 'text-amber-900',
  danger: 'text-rose-900',
  neutral: 'text-gray-500',
};

const PI_FAILURE = new Set(['ECHEC', 'FAILED', 'REFUSE', 'REFUSED']);

/**
 * @param {object|null|undefined} commande — commande / mapRdvToCommande
 * @returns {RdvPaymentTone}
 */
export function resolveRdvPaymentTone(commande) {
  const view = getRdvPaymentView(commande);
  if (!view) return 'neutral';

  const ps = String(view.paymentStatus || '').toUpperCase();
  const piStatut = String(
    commande?.paiementIntentStatut || commande?.paiementIntent?.statut || '',
  ).toUpperCase();
  const total = Number(view.total) || 0;
  const paid = Number(view.amountPaid) || 0;
  const dueLater = Number(view.amountDueLater) || 0;

  if (PI_FAILURE.has(piStatut)) return 'danger';
  if (ps === 'FAILED' || ps === 'PAYMENT_FAILED') return 'danger';

  if (view.isRefunded || ps === 'REFUNDED') return 'neutral';

  const fullyPaid =
    ps === 'PAID' || (total > 0 && paid >= total - 0.009) || (paid > 0 && dueLater <= 0.009);
  if (fullyPaid) return 'success';

  const needsFollowUp = commande ? rdvNeedsPaymentFollowUp(commande) : false;

  if (
    ps === 'AWAITING_PAYMENT' ||
    ps === 'ON_SITE' ||
    ps === 'UNPAID' ||
    ps === 'DEPOSIT_CAPTURED' ||
    dueLater > 0.009 ||
    needsFollowUp
  ) {
    return 'warning';
  }

  return 'neutral';
}

/**
 * @param {'chip' | 'cell' | 'badge' | 'panel' | 'panelTitle'} variant
 * @param {RdvPaymentTone} tone
 */
export function rdvPaymentToneClassName(variant, tone) {
  const key = tone || 'neutral';
  switch (variant) {
    case 'chip':
      return TONE_CHIP[key] || TONE_CHIP.neutral;
    case 'cell':
      return TONE_CELL[key] || TONE_CELL.neutral;
    case 'badge':
      return TONE_BADGE[key] || TONE_BADGE.neutral;
    case 'panel':
      return TONE_PANEL[key] || TONE_PANEL.neutral;
    case 'panelTitle':
      return TONE_PANEL_TITLE[key] || TONE_PANEL_TITLE.neutral;
    default:
      return TONE_BADGE.neutral;
  }
}
